import React, { useMemo, useState } from 'react';
import { Col, Row } from '@/lib/utils';
import { RegularText, DemiLightText } from '@/lib/Text';
import { Button } from '@/components/ui/button';
import {
    BrushType,
    ExceptionExclude,
    ExceptionInclude,
    WeekSchedule,
} from '../types/workingHours';
import { WorkBrushPalette, BrushOption, BUILT_IN_BRUSHES } from './WorkBrushPalette';
import { WorkGridTable } from './WorkGridTable';
import { WorkTemplateManager } from './WorkTemplateManager';
import { CopyFromPrevMonthDialog } from './CopyFromPrevMonthDialog';
import { useWorkTemplates } from '../hooks/useWorkTemplates';

type Coach = { coachId: string; coachName: string };

type Props = {
    centerId: string;
    coaches: Coach[];
    year: number;
    month: number;
    weekScheduleMap: Record<string, WeekSchedule>;
    exceptionIncludeMap: Record<string, ExceptionInclude[]>;
    exceptionExcludeMap: Record<string, ExceptionExclude[]>;
    onWeekScheduleChange: (coachId: string, next: WeekSchedule) => void;
    onIncludeMapChange: (next: Record<string, ExceptionInclude[]>) => void;
    onExcludeMapChange: (next: Record<string, ExceptionExclude[]>) => void;
    onWeekScheduleMapMerge: (partial: Record<string, WeekSchedule>) => void;
    isCoachModified: (coachId: string) => boolean;
};

export const UnifiedScheduleTab = (props: Props) => {
    const { templates, createTemplate, updateTemplate, deleteTemplate } = useWorkTemplates(props.centerId);

    const [selectedBrushId, setSelectedBrushId] = useState<string | null>(null);
    const [showTemplateManager, setShowTemplateManager] = useState(false);
    const [showCopyDialog, setShowCopyDialog] = useState(false);

    // 내장 + 저장된 템플릿을 합친 전체 옵션
    const selectedBrushOption: BrushOption | null = useMemo(() => {
        const builtIn = BUILT_IN_BRUSHES.find((o) => o.id === selectedBrushId);
        if (builtIn) return builtIn;
        if (selectedBrushId?.startsWith('tpl-')) {
            const tid = selectedBrushId.slice('tpl-'.length);
            const t = templates.find((tpl) => tpl.id === tid);
            if (!t) return null;
            if (t.type === 'include') {
                return {
                    id: selectedBrushId,
                    label: t.name,
                    brush: { kind: 'include', startTime: t.startTime, endTime: t.endTime, memo: t.memo },
                    accent: '#3D73DD',
                    bg: '#E6EEFC',
                    text: '#3D73DD',
                };
            }
            return {
                id: selectedBrushId,
                label: t.name,
                brush: { kind: 'exclude', memo: t.memo },
                accent: '#D93043',
                bg: '#FEEAEB',
                text: '#D93043',
            };
        }
        return null;
    }, [selectedBrushId, templates]);

    const selectedBrush: BrushType | null = selectedBrushOption?.brush ?? null;

    const handleSwapCoaches = (srcCoachId: string, dstCoachId: string) => {
        if (srcCoachId === dstCoachId) return;

        // weekScheduleMap 교환
        const emptyWeek: WeekSchedule = {
            월: { enabled: false, timeSlots: [] },
            화: { enabled: false, timeSlots: [] },
            수: { enabled: false, timeSlots: [] },
            목: { enabled: false, timeSlots: [] },
            금: { enabled: false, timeSlots: [] },
            토: { enabled: false, timeSlots: [] },
            일: { enabled: false, timeSlots: [] },
        };
        const srcWeek = props.weekScheduleMap[srcCoachId] ?? emptyWeek;
        const dstWeek = props.weekScheduleMap[dstCoachId] ?? emptyWeek;
        props.onWeekScheduleMapMerge({
            [srcCoachId]: dstWeek,
            [dstCoachId]: srcWeek,
        });

        // exceptionIncludeMap 교환
        const srcInc = props.exceptionIncludeMap[srcCoachId] ?? [];
        const dstInc = props.exceptionIncludeMap[dstCoachId] ?? [];
        props.onIncludeMapChange({
            ...props.exceptionIncludeMap,
            [srcCoachId]: dstInc,
            [dstCoachId]: srcInc,
        });

        // exceptionExcludeMap 교환
        const srcExc = props.exceptionExcludeMap[srcCoachId] ?? [];
        const dstExc = props.exceptionExcludeMap[dstCoachId] ?? [];
        props.onExcludeMapChange({
            ...props.exceptionExcludeMap,
            [srcCoachId]: dstExc,
            [dstCoachId]: srcExc,
        });
    };

    const handleCopyApplied = (result: {
        weekScheduleMap?: Record<string, WeekSchedule>;
        exceptionIncludeMap?: Record<string, ExceptionInclude[]>;
        exceptionExcludeMap?: Record<string, ExceptionExclude[]>;
    }) => {
        if (result.weekScheduleMap) {
            props.onWeekScheduleMapMerge(result.weekScheduleMap);
        }
        if (result.exceptionIncludeMap) {
            props.onIncludeMapChange({ ...props.exceptionIncludeMap, ...result.exceptionIncludeMap });
        }
        if (result.exceptionExcludeMap) {
            props.onExcludeMapChange({ ...props.exceptionExcludeMap, ...result.exceptionExcludeMap });
        }
        setShowCopyDialog(false);
    };

    return (
        <Col className="w-full gap-[14px]">
            {/* 상단: 액션 버튼 + 안내 */}
            <Row className="items-center justify-between">
                <Row className="gap-[8px]">
                    <Button
                        variant="ghost"
                        className="h-[36px] px-[14px] rounded-[10px] bg-[#F4F5F7] hover:bg-[#E8EAED]"
                        onClick={() => setShowCopyDialog(true)}
                    >
                        <RegularText fontSize={13} color="#4B5563">
                            이전 달 복사
                        </RegularText>
                    </Button>
                </Row>
                <DemiLightText fontSize={13} color="#6B7280">
                    시간을 클릭하면 설정 · 시간을 드래그하면 다른 강사와 바꾸기
                </DemiLightText>
            </Row>

            {/* 브러시 팔레트 */}
            <WorkBrushPalette
                templates={templates}
                selectedBrushId={selectedBrushId}
                onSelect={(opt) => setSelectedBrushId(opt?.id ?? null)}
                onOpenTemplateManager={() => setShowTemplateManager(true)}
            />

            {/* 그리드 */}
            <WorkGridTable
                coaches={props.coaches}
                year={props.year}
                month={props.month}
                weekScheduleMap={props.weekScheduleMap}
                exceptionIncludeMap={props.exceptionIncludeMap}
                exceptionExcludeMap={props.exceptionExcludeMap}
                onWeekScheduleChange={props.onWeekScheduleChange}
                onIncludeMapChange={props.onIncludeMapChange}
                onExcludeMapChange={props.onExcludeMapChange}
                isCoachModified={props.isCoachModified}
                selectedBrush={selectedBrush}
                onSwapCoaches={handleSwapCoaches}
            />

            {/* 범례 */}
            <Row className="items-center gap-[16px] mt-[4px]">
                <LegendDot color="#D6E4F7" text="기본 근무" />
                <LegendDot color="#ECEEF2" text="기본 휴무" />
                <LegendDot color="#C3E8CF" text="추가 근무 (IN)" />
                <LegendDot color="#F8C8CC" text="근무 제외 (OUT)" />
                <RegularText fontSize={13} color="#6B7280">
                    브러시 선택 후 셀에 클릭 또는 드래그로 적용
                </RegularText>
            </Row>

            {/* 템플릿 관리 모달 */}
            {showTemplateManager && (
                <WorkTemplateManager
                    templates={templates}
                    onCreate={createTemplate}
                    onUpdate={updateTemplate}
                    onDelete={deleteTemplate}
                    onClose={() => setShowTemplateManager(false)}
                />
            )}

            {/* 이전 달 복사 모달 */}
            {showCopyDialog && (
                <CopyFromPrevMonthDialog
                    centerId={props.centerId}
                    year={props.year}
                    month={props.month}
                    coachIds={props.coaches.map((c) => c.coachId)}
                    onCancel={() => setShowCopyDialog(false)}
                    onApplied={handleCopyApplied}
                />
            )}
        </Col>
    );
};

const LegendDot = ({ color, text }: { color: string; text: string }) => (
    <Row className="items-center gap-[6px]">
        <div
            className="w-[14px] h-[14px] rounded-[4px] border border-solid border-[#E6E8EA]"
            style={{ backgroundColor: color }}
        />
        <RegularText fontSize={13} color="#6B7280">
            {text}
        </RegularText>
    </Row>
);
